import type { EventEmitter } from "node:events";
import type WalletConnectProvider from "@walletconnect/ethereum-provider";
import type { Image } from "features/images";

type UserRole =
  | "admin"
  | "superAdmin"
  | "qa"
  | "OG Hugger"
  | "Hugger"
  | "Curator"
  | "Community Curator"
  | "curator-level-1"
  | "curator-level-2"
  | "curator-level-3";

/**
 * User - Same shape for Profile & User Search
 */
type User = {
  id: string;
  name?: string;
  email?: string;
  twitter?: string;
  discord?: string;
  discordId?: string;
  wallets: string[];
  roles: UserRole[];
  hugTokenIds: number[];
  hugImageIds: number[];
  utilities: string[];
  statsUpdatedAt?: string;
  utilitiesOverride: string[];
  lastStatsError?: string;
  avatarUrl?: string;
  avatar?: Image;
  completedLevel1Training?: boolean;
  completedLevel2Training?: boolean;
  completedLevel3Training?: boolean;
  rewardWallets: string[];
  guestPasses: string[];
  userInterests: string[];
  profileCompletedAt?: string;
};

interface DecodedJWT {
  iad?: string;
  user?: User;
  walletAddress?: string;
}

type MagicLink = {
  url: string;
};

/**
 * State object provided by AuthContext's value
 */
interface ConnectorState {
  chainId: number | null;
  accounts: string[] | null;
  isConnecting: boolean;
  error: Error | undefined;
}

/**
 * Actions that can be dispatched for AuthContext's reducer
 */
type ConnectorAction =
  | { type: "startConnecting" }
  | { type: "update"; chainId?: number; accounts?: string[] }
  | { type: "error"; error: Error | undefined }
  | { type: "reset" };

/**
 * Dispatch function for AuthContext's reducer
 */
type ConnectorDispatch = (action: ConnectorAction) => void;

interface RequestArgs {
  readonly method: string;
  readonly params?: unknown[] | object;
}

interface Provider extends EventEmitter {
  request(args: RequestArgs): Promise<unknown>;
}

type MetaMaskProvider = Provider & {
  isMetaMask?: boolean;
  isConnected?: () => boolean;
  providers?: MetaMaskProvider[];
};

type CoinBaseProvider = Provider & {
  selectedAddress?: string;
  providers?: CoinBaseProvider[];
};

type MockWalletConnectProvider = WalletConnectProvider & EventEmitter;

/**
 * Abstract wallet connector interface
 */
abstract class Connector {
  /**
   * A standard JavaScript Ethereum Provider API, see EIP-1193 or EIP-1102
   * If multiple provider extensions are installed, this won't necessarily be
   * equal to window.ethereum but will be one of the elements in the providers array
   */
  public provider: Provider | undefined;

  protected dispatch: ConnectorDispatch;

  /**
   * @param dispatch - Once AuthContext is mounted, this connector can be instantiated
   * with its dispatch function to update the reducer's state on changes to connection
   */
  constructor(dispatch: ConnectorDispatch) {
    this.dispatch = dispatch;
  }

  /**
   * Connect to wallet provider, should be initiated by user interaction
   */
  public abstract connect(...args: unknown[]): Promise<void> | void;

  /**
   * Attempt to connect on load, re-logging in if user has connected previously or failing silently
   */
  public abstract connectImmediately(...args: unknown[]): Promise<void> | void;

  /**
   * Psuedo-disconnect from the wallet provider. The only way to truly disconnect is via
   * the wallet provider interface, but we can prevent automatically reconnecting
   * on load by storing a cookie and checking for it before running connectImmediately.
   */
  public abstract disconnect(...args: unknown[]): Promise<void> | void;
}

export type {
  User,
  UserRole,
  DecodedJWT,
  CoinBaseProvider,
  ConnectorAction,
  ConnectorDispatch,
  ConnectorState,
  Provider,
  MetaMaskProvider,
  MockWalletConnectProvider,
  MagicLink,
};
export { Connector };
